/* eslint-disable no-console */
import { PublicKey } from '@solana/web3.js'
import { Side, UiLadder } from '@trathailoi/phoenix-orderbook-sdk'
import isEqual from 'lodash/isEqual'
import { createContext, useContext, useMemo } from 'react'

import SwapContext from '@/components/home/context'
import useFetchAllMarkets from '@/components/limit-order/hooks/use-fetch-all-markets'
import useFetchMarketInfo, { MarketInfoQueryResult } from '@/components/limit-order/hooks/use-fetch-market-info'
import { useSettings } from '@/hooks/use-settings'
import useTokens from '@/hooks/use-tokens'

type SwapOrderbookContextType = {
  marketAddress?: PublicKey
  priceLadder?: UiLadder
  orderbookSide?: Side
  marketInfo?: MarketInfoQueryResult
  isFetchingMarketInfo: boolean
  refetchMarketInfo: () => Promise<void>
}

const initContextState: SwapOrderbookContextType = {
  isFetchingMarketInfo: false,
  refetchMarketInfo: () => Promise.resolve(),
}

export const SwapOrderbookContext = createContext<SwapOrderbookContextType>(initContextState)

const SwapOrderbookProvider = ({ children }: HocProps) => {
  const { data: settingsData } = useSettings()
  const enabledSwapOrderbook = Boolean(settingsData?.swap_and_orderbook_router_feature_enabled)

  const { tokenIn: uiTokenIn, tokenOut: uiTokenOut } = useContext(SwapContext)
  const { tokenListMap } = useTokens(true)

  const {
    data: markets,
  } = useFetchAllMarkets(enabledSwapOrderbook)

  const marketAddress = useMemo(() => {
    if (!markets) return
    const market = markets.find(
      ({
        base_token_mint_address,
        quote_token_mint_address,
      }) => isEqual(
        [base_token_mint_address, quote_token_mint_address].sort(),
        [uiTokenIn?.address, uiTokenOut?.address].sort(),
      ),
    )

    if (!market) return

    return new PublicKey(market.market_address)
  }, [
    markets,
    uiTokenIn?.address,
    uiTokenOut?.address,
  ])

  const {
    data: marketInfo,
    isFetching: isFetchingMarketInfo,
    refetch: refetchMarketInfo,
  } = useFetchMarketInfo(marketAddress, enabledSwapOrderbook)

  const [baseToken] = useMemo(() => {
    return [
      marketInfo?.baseTokenMintAddress && tokenListMap.get(marketInfo.baseTokenMintAddress.toBase58()),
      marketInfo?.quoteTokenMintAddress && tokenListMap.get(marketInfo.quoteTokenMintAddress.toBase58()),
    ]
  }, [
    marketInfo,
    tokenListMap,
  ])

  const priceLadder = useMemo(() => marketInfo?.marketState?.getUiLadder(), [marketInfo?.marketState])

  const orderbookSide = useMemo(() => {
    if (!uiTokenIn || !baseToken) return
    return uiTokenIn.address === baseToken.address ? Side.Ask : Side.Bid
  }, [uiTokenIn, baseToken])

  const value = useMemo(() => {
    return {
      marketAddress,
      priceLadder,
      marketInfo,
      isFetchingMarketInfo,
      refetchMarketInfo: async () => {
        await refetchMarketInfo()
      },
      orderbookSide,
    }
  }, [marketAddress, orderbookSide, priceLadder, marketInfo, refetchMarketInfo, isFetchingMarketInfo])

  return (
    <SwapOrderbookContext.Provider value={value}>
      {children}
    </SwapOrderbookContext.Provider>
  )
}

export default SwapOrderbookProvider
